import {Component, Input} from "@angular/core";
import {ChartSeries} from "../chart-series";
import {AbstractChartComponent} from "../abstract-chart.component";
import * as Highcharts from "highcharts";
import {TranslateService} from "@ngx-translate/core";
import {MeasureDataType} from "../../measure-data-type";
import {DateTimeUtil} from "../../../utils/date-time-util";
import {SeriesBarOptions} from "highcharts";

@Component({
    selector: "bi-bar-chart",
    templateUrl: "./bar-chart.component.html",
    styleUrls: ["./bar-chart.component.scss"]
})
export class BarChartComponent extends AbstractChartComponent {

    @Input() selectedRefLine: string;
    @Input() tooltipOptions: Highcharts.TooltipOptions;

    private refValue: number;

    axis1dataType: MeasureDataType;
    axis2dataType: MeasureDataType;
    yLabel1: string;
    yLabel2: string;

    constructor(translateService: TranslateService) {
        super(translateService);
    }

    setupData(seriesDataArr: ChartSeries[]): void {
        if (seriesDataArr.length > 0) {
            this.axis1dataType = seriesDataArr[0].dataType;
            this.yLabel1 = seriesDataArr[0].yLabel || this.translateService.instant("wbo.bi.measure-type." + this.axis1dataType);
            const otherSeries: ChartSeries = seriesDataArr.find(s => s.dataType !== this.axis1dataType);
            if (otherSeries) {
                this.axis2dataType = otherSeries.dataType;
                this.yLabel2 = otherSeries.yLabel || this.translateService.instant("wbo.bi.measure-type." + this.axis2dataType);
            }
        }

        this.refValue = 0;
        if (this.selectedRefLine === "average") {
            if (seriesDataArr.length > 0 && seriesDataArr[0].data.length > 0) {
                let sum: number = 0;
                seriesDataArr[0].data.forEach((n: number) => {
                    sum = sum + n;
                });

                this.refValue = sum / seriesDataArr[0].data.length;
            }
        }
    }

    setupChart(seriesDataArr: ChartSeries[]): void {
        seriesDataArr.forEach((series: ChartSeries) => {
            if (this.axis2dataType && this.axis2dataType === series.dataType) {
                series.yAxis = 1;
            }
            series.type = "bar";
            this.chart.addSeries(<SeriesBarOptions> series);
        });
    }

    defineChart(): Highcharts.Options {
        const chartThis = this;

        const yAxis = [];
        const yDef = {
            title: {
                text: this.yLabel1,
            },
            labels: {
                formatter: function () {
                    if (chartThis.axis1dataType === MeasureDataType.SECONDS) {
                        return DateTimeUtil.formatTimeForSeconds(this.value);
                    }

                    return this.axis.defaultLabelFormatter.call(this);
                }
            },
            plotLines: []
        };

        if (this.refValue) {
            yDef.plotLines = [{
                color: "grey",
                dashStyle: "dash",
                value: this.refValue,
                width: 2,
                zIndex: 10
            }];
        }
        yAxis.push(yDef);

        if (this.axis2dataType) {
            const yDef2 = {
                title: {
                    text: this.yLabel2,
                },
                labels: {
                    formatter: function () {
                        if (chartThis.axis2dataType === MeasureDataType.SECONDS) {
                            return DateTimeUtil.formatTimeForSeconds(this.value);
                        }

                        return this.axis.defaultLabelFormatter.call(this);
                    }
                },
                plotLines: [],
                opposite: true
            };

            yAxis.push(yDef2);
        }

        return {
            chart: {
                height: this.height,
                type: chartThis.chartType,
                events: {
                    // Custom selection handler that selects points and cancels the default zoom behaviour
                    selection: function (e) {
                        chartThis.drillToSelectionEvent(e);
                        return false; // disable zoom
                    }
                },
                zooming: {
                    type: "xy"
                },
            },
            colors: this.colors,
            title: {
                style: {fontSize: "14px", fontWeight: "bold"},
                align: "left",
                x: this.titleIndent,
                text: this.title,
            },
            xAxis: [{
                title: {
                    text: this.xAxisLabel,
                }
            }],
            yAxis: yAxis,
            legend: {
                align: "right",
                y: this.legendVerticalAlign === "top" ? -5 : 0,
                x: this.legendVerticalAlign === "top" ? -30 : 0,
                verticalAlign: this.legendVerticalAlign,
                floating: this.legendVerticalAlign === "top",
                backgroundColor: "white",
                borderColor: "#CCC",
                itemStyle: {fontWeight: "normal"},
                borderWidth: 0,
                shadow: false,
                margin: 5
            },
            tooltip: this.tooltipOptions ? this.tooltipOptions : {
                headerFormat: "<b>{point.x}</b><br/>",
                pointFormatter: function () {
                    if (chartThis.seriesDataTypes[this.series.name] === MeasureDataType.SECONDS) {
                        return this.series.name + ": " + DateTimeUtil.formatTimeForSeconds(this.y);
                    }

                    return this.series.name + ": " + (this.y).toLocaleString();
                }
            },
            plotOptions: {
                series: {
                    cursor: "pointer",
                    point: {
                        events: {
                            click: function () {
                                chartThis.selectSingle(this.index);
                                return true;
                            }
                        }
                    }
                }
            },
            credits: {
                enabled: false
            }
        };
    }
}
